import { Injectable } from '@angular/core';
import { ResultsetItem } from '@models/resultset-item';
import { BehaviorSubject } from 'rxjs';
import { ResultsStoreService } from './results-store.service';
import { ResultsService } from './results.service';

export interface View {
  type?: ViewType;
  tabName?: string;
  Run_Date?: string;
  scenario?: number;
}

export type ViewType = 'summary' | 'detail';

export const NULL_TAB = { type: null, tabName: null };
export const HIGHLIGHTS_TAB = 'Highlights';

@Injectable({ providedIn: 'root' })
export class ResultTabsService {
  private _activeView$: BehaviorSubject<View> = new BehaviorSubject<View>(NULL_TAB);
  private _tabCollection$: BehaviorSubject<ResultsetItem[]>;

  public selectedTab: View = null;

  get lastClosedResultset() {
    return this.resultsStore.lastClosedResultset;
  }

  public get activeView$() {
    return this._activeView$.asObservable();
  }
  public get activeView(): View {
    return this._activeView$.getValue();
  }

  public set activeView(view: View) {
    if (this.activeView.tabName !== view.tabName || this.activeView.type !== view.type) {
      this._activeView$.next(view);
    }
  }

  public get tabCollection$(): BehaviorSubject<ResultsetItem[]> {
    return this._tabCollection$;
  }
  public set tabCollection$(value: BehaviorSubject<ResultsetItem[]>) {
    this._tabCollection$ = value;
  }

  constructor(private resultsStore: ResultsStoreService, private resultsService: ResultsService) {
    this.activeView = NULL_TAB;
    this.tabCollection$ = new BehaviorSubject<ResultsetItem[]>([]);
    this.resultsStore.resultsList$.subscribe(resultList => {
      const tabList: ResultsetItem[] = this.resultsStore.getResultTree();
      this.tabCollection$.next(tabList);
      const currentTabName = this.activeView.tabName;
      if (currentTabName) {
        const flatTabs: ResultsetItem[] = [];
        tabList.forEach((base: ResultsetItem) => {
          flatTabs.push(base);
          if (base.hasOwnProperty('StressList') && base.StressList.length > 0) {
            base.StressList.forEach((stress: ResultsetItem) => flatTabs.push(stress));
          }
        });
        const found = flatTabs.find(res => currentTabName === res.Mnemonic && res.Hidden === false);
        if (!found) {
          this.activeView = {
            tabName: HIGHLIGHTS_TAB,
            Run_Date: null,
          };
        } else if (this.activeView.Run_Date && found.Run_Date && new Date(this.activeView.Run_Date) < new Date(found.Run_Date)) {
          this._activeView$.next({ ...this.activeView, Run_Date: found.Run_Date });
        }
      }
    });
  }

  public selectTab(view: View) {
    const currentView = this.activeView;
    const info: ResultsetItem = this.resultsStore.resultsList.find((result: ResultsetItem) => result.Mnemonic === view.tabName);

    if (info) {
      view.Run_Date = info.Run_Date || null;
      view.scenario = info.Scenario_ID || null;
    }

    const nextView: View = {
      ...currentView,
      ...view,
    };

    if (!nextView.type) {
      nextView.type = 'summary';
    }

    const spreadSheetName =
      nextView.tabName !== HIGHLIGHTS_TAB && nextView.type === 'summary' ? `${nextView.tabName}-summary` : nextView.tabName;

    this.resultsService.spread.setActiveSheet(spreadSheetName);
    this.activeView = nextView;
    this.selectedTab = nextView;
  }

  public resetTabs() {
    this.activeView = NULL_TAB;
  }
}
